import { type Accessor, createEffect, createSignal, onCleanup } from "solid-js";

const lengthToPx = (value: string) => {
	const length = Number.parseFloat(value) || 0;
	if (value.trim().endsWith("px")) {
		return length;
	}
	const rootSize = Number.parseFloat(
		getComputedStyle(document.documentElement).fontSize,
	);
	return length * (rootSize || 16);
};

type Options = {
	/** The pinned bar; the element after it is the content it can cover. */
	bar: Accessor<HTMLElement | undefined>;
	/** Off, the bar stays expanded and nothing is watched. */
	enabled: Accessor<boolean>;
	/**
	 * Custom property on the bar holding its whole collapsed height, padding
	 * included.
	 */
	collapsedHeightProperty: string;
};

/**
 * A pinned bar that collapses whenever, collapsed, it would leave no gap above
 * the content after it, and expands when it would. Only a change in that
 * coverage updates the state, so a toggle by hand holds until the next one.
 */
export function useCollapsibleBar(options: Options) {
	const [collapsed, setCollapsed] = createSignal(false);
	// Whether the collapsed bar keeps its expanded height in the page flow.
	// Collapsing over content has to, or that content would move and change
	// whether the bar should be collapsed. Collapsing by hand over nothing
	// gives the space back instead of leaving a gap. Decided once per
	// collapse: switching it while collapsed would move the content too.
	const [holdsSpace, setHoldsSpace] = createSignal(false);
	let covered: boolean | undefined;

	const collapse = (holdSpace: boolean) => {
		setHoldsSpace(holdSpace);
		setCollapsed(true);
	};
	const expand = () => {
		setCollapsed(false);
		setHoldsSpace(false);
	};

	const recheck = () => {
		const el = options.bar();
		const content = el?.nextElementSibling;
		if (!options.enabled() || !el || !content) {
			return;
		}
		const style = getComputedStyle(el);
		const collapsedBottom =
			el.getBoundingClientRect().top +
			lengthToPx(style.getPropertyValue(options.collapsedHeightProperty)) +
			(Number.parseFloat(style.borderBottomWidth) || 0);
		const now = content.getBoundingClientRect().top <= collapsedBottom;
		if (now === covered) {
			return;
		}
		covered = now;
		if (!now) {
			expand();
		} else if (!collapsed()) {
			collapse(true);
		}
	};

	createEffect(() => {
		covered = undefined;
		if (!options.enabled()) {
			expand();
			return;
		}
		// Captured on the document because it is the page layout, not the bar,
		// that decides which element scrolls.
		document.addEventListener("scroll", recheck, {
			capture: true,
			passive: true,
		});
		window.addEventListener("resize", recheck);
		onCleanup(() => {
			document.removeEventListener("scroll", recheck, { capture: true });
			window.removeEventListener("resize", recheck);
		});
	});

	return {
		collapsed: () => options.enabled() && collapsed(),
		holdsSpace: () => options.enabled() && collapsed() && holdsSpace(),
		toggle: () => (collapsed() ? expand() : collapse(covered === true)),
		/** For a change the bar can't see, such as the bars above it resizing. */
		recheck,
	};
}
